import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { perfilamientoService } from '@/features/perfilamiento/services/perfilamiento.service'
import type { StartPerfilamientoResponse } from '@/features/perfilamiento/services/perfilamiento.types'
import { persistPerfilamientoFilterQuestion } from '@/features/perfilamiento/store/perfilamiento.store'
import { PerfilamientoConsentModal } from '@/features/perfilamiento/components/PerfilamientoConsentModal'
import { usePerfilamientoError } from '@/features/perfilamiento/components/PerfilamientoErrorModal'
import { Button } from '@/shared/components/ui/Button'
import logoNegroSrc from '@/assets/LogoNegro.png'

export function PerfilamientoEntryPage() {
  const { t, i18n } = useTranslation('perfilamiento')
  const navigate = useNavigate()
  const { showError } = usePerfilamientoError()
  const [isStarting, setIsStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [showConsentModal, setShowConsentModal] = useState(true)
  const [pendingFilter, setPendingFilter] = useState<StartPerfilamientoResponse | null>(null)

  async function handleStart() {
    if (isStarting) return

    setIsStarting(true)
    setStartError(null)

    const result = await perfilamientoService.start(
      {
        channel: 1,
        consentAccepted: true,
        consentVersion: '1.0',
        previousProcessId: null,
      },
      i18n.language,
    )

    setIsStarting(false)

    if (!result.success) {
      setStartError(result.message)
      showError(result.message, result.errorCode)
      return
    }

    const payload = result.data as StartPerfilamientoResponse
    persistPerfilamientoFilterQuestion(payload.processId, payload.filterQuestion)
    setPendingFilter(payload)
  }

  function handleContinueToFilter() {
    if (!pendingFilter) return

    navigate(`/perfilamiento/filtro/${pendingFilter.processId}`, {
      replace: true,
      state: { filterQuestion: pendingFilter.filterQuestion },
    })
  }

  return (
    <section className="flex min-h-[calc(100svh-8rem)] items-center justify-center px-4 py-8 md:px-8 md:py-10">
      <div className="flex w-full max-w-[760px] flex-col items-center text-center">
        <div className="mx-auto w-[252px] md:w-[321px]">
          <img alt="SynerGy" className="h-auto w-full" src={logoNegroSrc} />
        </div>

        <h1 className="mt-12 max-w-[42rem] font-[var(--font-heading)] text-[46px] font-medium leading-[52px] text-[#003D66]">
          <span className="block">{t('landing.kicker.first')}</span>
          <span className="block text-[#0066AA]">{t('landing.kicker.second')}</span>
        </h1>

        <p className="mt-8 max-w-[28rem] font-[var(--font-heading)] text-[26px] font-medium leading-[30px] text-[#003D66]">
          {t('landing.description')}
        </p>

        {startError ? (
          <p className="mt-8 rounded-xl border border-[#ffd4d8] bg-[#fff5f6] px-4 py-3 text-sm text-[#8a1f2d]">
            {startError}
          </p>
        ) : null}

        {pendingFilter ? (
          <Button
            className="mt-12 flex h-[48px] w-[353px] items-center justify-center rounded-[8px] bg-[#0066AA] px-5 py-2 text-white hover:bg-[#005c99]"
            disabled={isStarting}
            aria-busy={isStarting}
            onClick={handleContinueToFilter}
            fullWidth={false}
          >
            {t('entry.continue')}
          </Button>
        ) : null}
      </div>

      <PerfilamientoConsentModal
        open={showConsentModal}
        isLoading={isStarting}
        onClose={() => setShowConsentModal(false)}
        onAccept={() => {
          setShowConsentModal(false)
          void handleStart()
        }}
      />
    </section>
  )
}
